import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useTrail } from '@/hooks/use-reblogs';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Repeat2, MessageSquareQuote, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TrailLensDialogProps {
  postId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TrailLensDialog: React.FC<TrailLensDialogProps> = ({ postId, open, onOpenChange }) => {
  const { data, isLoading } = useTrail(postId);
  const navigate = useNavigate();

  const typeMeta = (t?: string) => {
    const type = t || '';
    if (type === 'reblog') {
      return { label: 'Reloop', icon: <Repeat2 size={14} />, className: 'text-violet-400 border-violet-400/30 bg-violet-500/10' };
    }
    if (type === 'quote') {
      return { label: 'Quote', icon: <MessageSquareQuote size={14} />, className: 'text-cyan-400 border-cyan-400/30 bg-cyan-500/10' };
    }
    if (type === 'remix') {
      return { label: 'Remix', icon: <Sparkles size={14} />, className: 'text-amber-400 border-amber-400/30 bg-amber-500/10' };
    }
    return { label: type, icon: null as React.ReactNode, className: 'text-muted-foreground border-border bg-muted/20' };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Trail Lens</DialogTitle>
          <DialogDescription>
            Full history of reloops, quotes, and remixes for this post.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1 custom-scrollbar">
          {isLoading && <div className="text-sm text-muted-foreground">Loading trail...</div>}
          {!isLoading && (!data || data.length === 0) && (
            <div className="text-sm text-muted-foreground">No activity yet.</div>
          )}
          {data?.map((r) => {
            const t = typeMeta(r.type);
            const username = r.user?.username;
            const display = r.user?.display_name || username || 'Unknown';
            const goProfile = () => { if (username) navigate(`/profile/${username}`); };
            return (
              <div key={r.id} className="flex items-start gap-3 p-3 rounded-lg border bg-card/60">
                <button onClick={goProfile} className="shrink-0" aria-label={`Open @${username} profile`}>
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={r.user?.profile_image_url || undefined} />
                    <AvatarFallback className="text-xs">
                      {r.user?.display_name?.[0] || r.user?.username?.[0] || '?'}
                    </AvatarFallback>
                  </Avatar>
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={goProfile} className="font-medium hover:underline truncate">
                      {display}
                    </button>
                    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] leading-none', t.className)}>
                      {t.icon}
                      {t.label}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
                    </span>
                  </div>
                  {r.content && (
                    <div className="text-sm mt-2 whitespace-pre-wrap">
                      {r.content}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TrailLensDialog;
